﻿import React, { useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { ExchangeableProduct } from '../EditPage';
import { useClickOutside } from '../hooks/clickOutside';
import ConfirmationModal from './ConfirmationModal';
import { AddIcon, AdditionalProductIcon, CameraIcon, CheckmarkIcon } from './Icons';
import { IExchangeableProductButton } from './AddedProducts';
import { ParseTranslations, TranslatedLabels } from '../../../../../shared/types';

type EditAddedProductButtonProps = {
    exchangeableProducts: IExchangeableProductButton[];
    labels: TranslatedLabels;
    parseTranslations: ParseTranslations;
    onChange: (product: ExchangeableProduct) => void;
};

type ExchangeableProductButtonProps = {
    onClick: () => void;
    product: IExchangeableProductButton;
};

function ExchangeableProductButton({ onClick, product }: ExchangeableProductButtonProps) {
    const [isHover, setIsHover] = useState<boolean>(false);

    function handleClick() {
        onClick();
    }

    function handleOnMouseLeave() {
        setIsHover(false);
    }

    function handleOnMouseEnter() {
        setIsHover(true);
    }

    return (
        <button
            className={`addedProducts__changeProductDropdownProductButton${
                product.disabled ? '--selected' : ''
            }`}
            onClick={handleClick}
            disabled={product.disabled}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
        >
            {product.disabled ? (
                <CheckmarkIcon />
            ) : product.isAnExtraProduct ? (
                <AdditionalProductIcon />
            ) : (
                <CameraIcon />
            )}
            {!product.disabled && isHover ? <AddIcon /> : null}
            <span className="addedProducts__changeProductDropdownButtonLabel">{product.name}</span>
        </button>
    );
}

function ChangeAddedProductButton({
    exchangeableProducts,
    labels,
    parseTranslations,
    onChange
}: EditAddedProductButtonProps) {
    const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
    const [activeProduct, setActiveProduct] = useState<ExchangeableProduct | null>(null);
    const [showChangeProductConfirmationModal, setShowChangeProductConfirmationModal] =
        useState<boolean>(false);
    const changeProductButtonRef = useRef<HTMLButtonElement | null>(null);

    function handleClickChangeProductButton() {
        setIsDropdownOpen((isOpen) => !isOpen);
    }

    function handleClickExchangeableProductButtonFactory(product: ExchangeableProduct) {
        return function handleClickExchangeableProductButton() {
            setIsDropdownOpen(false);
            setActiveProduct(product);
            setShowChangeProductConfirmationModal(true);
        };
    }

    function handleCancelChangeProduct() {
        setShowChangeProductConfirmationModal(false);
    }

    function handleConfirmChangeProduct() {
        setShowChangeProductConfirmationModal(false);
        if (activeProduct) {
            onChange(activeProduct);
        }
    }

    function handleClickOutside() {
        if (isDropdownOpen) {
            setIsDropdownOpen(false);
        }
    }

    useClickOutside(changeProductButtonRef.current, handleClickOutside, [isDropdownOpen]);

    return (
        <div className="addedProducts__changeProductContainer">
            <button
                className="addedProducts__changeProductButton"
                ref={changeProductButtonRef}
                onClick={handleClickChangeProductButton}
                aria-label={labels.editButtonLabel}
            >
                <i className="material-icons addedProducts__changeProductButtonIcon">edit</i>
                <span>{labels.editButtonLabel}</span>
            </button>
            {isDropdownOpen ? (
                <div className="addedProducts__changeProductDropdownContainer">
                    {exchangeableProducts.map((product) => {
                        return (
                            <ExchangeableProductButton
                                key={product.id}
                                product={product}
                                onClick={handleClickExchangeableProductButtonFactory(product)}
                            />
                        );
                    })}
                </div>
            ) : null}
            <ConfirmationModal
                isOpen={showChangeProductConfirmationModal}
                title={labels.changeOrderRow__confirmationModal__title}
                onClose={handleCancelChangeProduct}
                textContent={
                    activeProduct ? (
                        <p>
                            {parseTranslations.changeOrderRow__confirmationModal__text(
                                activeProduct.name
                            )}
                        </p>
                    ) : null
                }
            >
                <Button variant="secondary" onClick={handleCancelChangeProduct}>
                    {labels.changeOrderRow__confirmationModal__cancelButtonLabel}
                </Button>
                <Button
                    className="editPhotoOrder__confirmationModal__confirmButton"
                    variant="primary"
                    onClick={handleConfirmChangeProduct}
                >
                    {labels.changeOrderRow__confirmationModal__confirmButtonLabel}
                </Button>
            </ConfirmationModal>
        </div>
    );
}

export default ChangeAddedProductButton;
